import React from 'react';
import { fromLonLat } from 'ol/proj';
import debounce from 'lodash/debounce';
import {
  AutoComplete,
  DropDownList,
  AutoCompleteCloseEvent,
  AutoCompleteChangeEvent
} from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';
import { RadioButton } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

import { Place } from '../../types/place';
import { MapContext } from './map-context';
import { SearchPlaceAsync } from '../../services/maps';

const contentOptions = [
  { text: 'All content', value: 'all' },
  { text: 'No content', value: 'none' },
  { text: 'Only zones', value: 'zones' },
  { text: 'Only areas', value: 'areas' },
  { text: 'Only tasks', value: 'tasks' },
  { text: 'Only images', value: 'images' }
];

const isValidCoordinates = (value: string) => {
  const regexExp = /^((\-?|\+?)?\d+(\.\d+)?),\s*((\-?|\+?)?\d+(\.\d+)?)$/gi;
  return regexExp.test(value);
};

export const MapLocationDialog: React.FC<{
  onClose: () => void;
  onRecentreLocation: (
    {
      lon,
      lat
    }: {
      lon: number;
      lat: number;
    },
    contentType: 'zones' | 'tasks' | 'areas' | 'all' | 'none' | 'images'
  ) => Promise<void>;
}> = ({ onClose, onRecentreLocation }) => {
  const map = React.useContext(MapContext);

  const [locationType, setLocationType] = React.useState<'search' | 'current'>(
    'search'
  );
  const [searchResult, setSearchResult] = React.useState<Place[]>([]);
  const [selectedSearchResult, setSelectedSearchResult] = React.useState('');
  const [selectedContent, setSelectedContent] = React.useState(
    contentOptions[0]
  );

  const onSearchCoordinates = React.useCallback(
    (lat: string, lon: string) => {
      map.getView().setCenter(fromLonLat([parseFloat(lon), parseFloat(lat)]));
    },
    [map]
  );

  const fetchPlaces = React.useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          const response = await SearchPlaceAsync(value);
          if (response) {
            setSearchResult(response.data as Place[]);
          }
        } catch (error) {
          console.error(error);
        }
      }, 350),
    []
  );

  const onAutoCompleteChangeHandler = React.useCallback(
    (e: AutoCompleteChangeEvent) => {
      if (!e.value || e.syntheticEvent.type === 'click') {
        return;
      } else if ((e.syntheticEvent as any).key === 'Enter') {
        if (isValidCoordinates(e.value)) {
          const coord = e.value.split(',');
          onSearchCoordinates(coord[0], coord[1]);
        }
      } else {
        fetchPlaces(e.value);
      }
    },
    [fetchPlaces, onSearchCoordinates]
  );

  const onAutoCompleteCloseHandler = React.useCallback(
    (e: AutoCompleteCloseEvent) => {
      setSelectedSearchResult(e.target.value);
    },
    []
  );

  const getCurrentPosition = (): Promise<{ lon: number; lat: number }> =>
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({ lon: pos.coords.longitude, lat: pos.coords.latitude }),
        (err) => reject(err)
      );
    });

  const handleGo = async () => {
    const lonLat = { lon: 0, lat: 0 };

    if (locationType === 'current') {
      const position = await getCurrentPosition();
      lonLat.lon = position.lon;
      lonLat.lat = position.lat;
    } else {
      const selected = searchResult.find(
        (e) => e.display_name === selectedSearchResult
      );
      if (selected) {
        lonLat.lon = selected.lon;
        lonLat.lat = selected.lat;
      }
    }

    onRecentreLocation(lonLat, selectedContent.value as 'none');
    onClose();
  };

  return (
    <Dialog title="Location" onClose={onClose} width={400}>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '10px' }}>
          <RadioButton
            name="locationType"
            value="current"
            checked={locationType === 'current'}
            label="My current location"
            onChange={() => setLocationType('current')}
          />
        </div>
        <div>
          <RadioButton
            name="locationType"
            value="search"
            checked={locationType === 'search'}
            label="Search location"
            onChange={() => setLocationType('search')}
          />
        </div>
      </div>

      {locationType === 'search' && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '13px',
              marginBottom: '6px',
              color: '#333'
            }}>
            Search
          </label>
          <AutoComplete
            data={searchResult.map((p) => p.display_name)}
            onChange={onAutoCompleteChangeHandler}
            onClose={onAutoCompleteCloseHandler}
            placeholder={'Search'}
          />
        </div>
      )}

      <div style={{ marginBottom: '8px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            marginBottom: '6px',
            color: '#333'
          }}>
          Select which content moves when the map is relocated.
        </label>
        <DropDownList
          data={contentOptions}
          textField={'text'}
          dataItemKey={'value'}
          value={selectedContent}
          onChange={(e) => setSelectedContent(e.value)}
          style={{ width: '100%' }}
        />
      </div>

      <DialogActionsBar>
        <Button onClick={onClose} themeColor={'base'}>
          Cancel
        </Button>
        <Button onClick={handleGo} themeColor={'success'}>
          Go
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
